<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainAssistantsAPI from 'dashboard/api/captainAssistants';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ButtonV4 from 'next/button/Button.vue';
import DynamicListField from './DynamicListField.vue';

const { t } = useI18n();

const isFetching = ref(true);
const isSaving = ref(false);
const fetchError = ref(false);

const assistantId = ref(null);
const name = ref('');
const description = ref('');
const productName = ref('');
const instructions = ref('');
const welcomeMessage = ref('');
const handoffMessage = ref('');
const guardrails = ref([]);
const responseGuidelines = ref([]);
const aiAutoReplyEnabled = ref(false);
const maxAiAttempts = ref(5);
const maxAttemptsPresets = [
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 0, label: 'Unlimited' },
];

const populateFromAssistant = assistant => {
  assistantId.value = assistant.id;
  name.value = assistant.name || '';
  description.value = assistant.description || '';
  productName.value = assistant.config?.product_name || '';
  instructions.value = assistant.config?.instructions || '';
  welcomeMessage.value = assistant.config?.welcome_message || '';
  handoffMessage.value = assistant.config?.handoff_message || '';
  guardrails.value = assistant.guardrails?.length ? [...assistant.guardrails] : [];
  responseGuidelines.value = assistant.response_guidelines?.length
    ? [...assistant.response_guidelines]
    : [];
  aiAutoReplyEnabled.value = assistant.config?.ai_auto_reply_enabled === true;
  // Default to 5 when the assistant has never set a cap. 0 means unlimited.
  maxAiAttempts.value =
    assistant.config?.max_ai_attempts === undefined
      ? 5
      : Number(assistant.config.max_ai_attempts);
};

const fetchAssistant = async () => {
  isFetching.value = true;
  fetchError.value = false;
  try {
    const response = await CaptainAssistantsAPI.get();
    const [existing] = response.data.payload;
    if (existing) populateFromAssistant(existing);
  } catch (error) {
    fetchError.value = true;
  } finally {
    isFetching.value = false;
  }
};

const buildPayload = () => ({
  assistant: {
    name: name.value.trim(),
    description: description.value.trim(),
    // Only non-empty lines - an empty text box left by "+ Add" but
    // never filled in shouldn't become a blank rule the LLM sees.
    guardrails: guardrails.value.map(g => g.trim()).filter(Boolean),
    response_guidelines: responseGuidelines.value
      .map(g => g.trim())
      .filter(Boolean),
    config: {
      product_name: productName.value.trim(),
      instructions: instructions.value.trim(),
      welcome_message: welcomeMessage.value.trim(),
      handoff_message: handoffMessage.value.trim(),
      ai_auto_reply_enabled: aiAutoReplyEnabled.value,
      // Clamp locally so an accidental negative never reaches the API.
      // Backend re-clamps to 0..100 in Captain::Assistant#max_ai_attempts=.
      max_ai_attempts: Math.max(0, Number(maxAiAttempts.value) || 0),
    },
  },
});

const handleSave = async () => {
  if (!name.value.trim() || !description.value.trim()) {
    useAlert(t('AI_AGENT_SETTINGS.BRAIN.VALIDATION_ERROR'));
    return;
  }
  isSaving.value = true;
  try {
    const response = assistantId.value
      ? await CaptainAssistantsAPI.update(assistantId.value, buildPayload())
      : await CaptainAssistantsAPI.create(buildPayload());
    populateFromAssistant(response.data);
    useAlert(t('AI_AGENT_SETTINGS.BRAIN.SAVED'));
  } catch (error) {
    useAlert(
      error.response?.data?.message || t('AI_AGENT_SETTINGS.BRAIN.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchAssistant);
</script>

<template>
  <div v-if="isFetching" class="text-sm text-n-slate-11">
    {{ t('AI_AGENT_SETTINGS.LOADING') }}
  </div>
  <p v-else-if="fetchError" class="text-sm text-n-ruby-11">
    {{ t('AI_AGENT_SETTINGS.ERRORS.FETCH') }}
  </p>
  <div v-else class="flex flex-col gap-6 max-w-2xl">
    <label
      class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4 cursor-pointer hover:bg-n-alpha-2 transition-colors"
    >
      <input
        v-model="aiAutoReplyEnabled"
        type="checkbox"
        class="mt-0.5 h-4 w-4 cursor-pointer"
      />
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENT_SETTINGS.BRAIN.AUTO_REPLY_LABEL') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('AI_AGENT_SETTINGS.BRAIN.AUTO_REPLY_HELP') }}
        </span>
      </div>
    </label>

    <div
      class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENT_SETTINGS.BRAIN.MAX_ATTEMPTS_LABEL') }}
      </span>
      <span class="text-xs text-n-slate-11">
        {{ t('AI_AGENT_SETTINGS.BRAIN.MAX_ATTEMPTS_HELP') }}
      </span>
      <div class="mt-1 flex items-center gap-2">
        <input
          v-model.number="maxAiAttempts"
          type="number"
          min="0"
          max="100"
          step="1"
          class="w-32 rounded border border-n-weak px-3 py-2 text-sm text-n-slate-12 bg-n-alpha-1"
        />
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="preset in maxAttemptsPresets"
            :key="preset.value"
            type="button"
            class="px-2.5 py-1 rounded-full border text-xs transition-colors"
            :class="
              maxAiAttempts === preset.value
                ? 'border-n-brand bg-n-brand/10 text-n-brand font-medium'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="maxAiAttempts = preset.value"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>
      <p
        v-if="maxAiAttempts === 0"
        class="text-xs text-n-slate-11 mt-1 italic"
      >
        {{ t('AI_AGENT_SETTINGS.BRAIN.MAX_ATTEMPTS_UNLIMITED_HINT') }}
      </p>
    </div>

    <div class="grid sm:grid-cols-2 gap-4">
      <Input
        v-model="name"
        :label="t('AI_AGENT_SETTINGS.BRAIN.NAME_LABEL')"
        :placeholder="t('AI_AGENT_SETTINGS.BRAIN.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="productName"
        :label="t('AI_AGENT_SETTINGS.BRAIN.PRODUCT_LABEL')"
        :placeholder="t('AI_AGENT_SETTINGS.BRAIN.PRODUCT_PLACEHOLDER')"
      />
    </div>
    <TextArea
      v-model="description"
      :label="t('AI_AGENT_SETTINGS.BRAIN.DESCRIPTION_LABEL')"
      :placeholder="t('AI_AGENT_SETTINGS.BRAIN.DESCRIPTION_PLACEHOLDER')"
      :max-length="500"
      show-character-count
    />
    <TextArea
      v-model="instructions"
      :label="t('AI_AGENT_SETTINGS.BRAIN.INSTRUCTIONS_LABEL')"
      :placeholder="t('AI_AGENT_SETTINGS.BRAIN.INSTRUCTIONS_PLACEHOLDER')"
      :max-length="4000"
      show-character-count
      auto-height
      min-height="8rem"
      max-height="20rem"
    />
    <div class="grid sm:grid-cols-2 gap-4">
      <Input
        v-model="welcomeMessage"
        :label="t('AI_AGENT_SETTINGS.BRAIN.WELCOME_LABEL')"
        :placeholder="t('AI_AGENT_SETTINGS.BRAIN.WELCOME_PLACEHOLDER')"
      />
      <Input
        v-model="handoffMessage"
        :label="t('AI_AGENT_SETTINGS.BRAIN.HANDOFF_LABEL')"
        :placeholder="t('AI_AGENT_SETTINGS.BRAIN.HANDOFF_PLACEHOLDER')"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENT_SETTINGS.BRAIN.GUARDRAILS_LABEL') }}
      </label>
      <p class="text-xs text-n-slate-11 mb-1">
        {{ t('AI_AGENT_SETTINGS.BRAIN.GUARDRAILS_HELP') }}
      </p>
      <DynamicListField
        v-model="guardrails"
        :placeholder="t('AI_AGENT_SETTINGS.BRAIN.GUARDRAILS_PLACEHOLDER')"
        :add-label="t('AI_AGENT_SETTINGS.BRAIN.ADD_RULE')"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENT_SETTINGS.BRAIN.RESPONSE_GUIDELINES_LABEL') }}
      </label>
      <p class="text-xs text-n-slate-11 mb-1">
        {{ t('AI_AGENT_SETTINGS.BRAIN.RESPONSE_GUIDELINES_HELP') }}
      </p>
      <DynamicListField
        v-model="responseGuidelines"
        :placeholder="t('AI_AGENT_SETTINGS.BRAIN.RESPONSE_GUIDELINE_PLACEHOLDER')"
        :add-label="t('AI_AGENT_SETTINGS.BRAIN.ADD_GUIDELINE')"
      />
    </div>
    <ButtonV4
      solid
      blue
      class="self-start"
      :is-loading="isSaving"
      :label="t('AI_AGENT_SETTINGS.BRAIN.SAVE_BUTTON')"
      @click="handleSave"
    />
  </div>
</template>
